import { createContext, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { cn } from './lib';
import {
  DEFAULT_DATE_FORMAT,
  dayInWords,
  formatHint,
  monthInWords,
  readTypedDate,
  writeDay,
  type DateFormat,
} from './typedDate';
import './Field.css';
import './DateField.css';

/* ── A date, typed the way the hospital writes dates (item 161) ──────────

   The browser's own date input draws whatever its locale says, so the same
   field read 03/04/2027 as the third of April on one desk and the fourth of
   March on the next, and a storekeeper typing an expiry off a carton had no
   way to tell. This field takes the date as the hospital (or the person)
   writes dates, and says it back with the month in words as it is typed:
   "3 Apr 2027". A day and a month can no longer be swapped without the
   person seeing it.

   The value in and out is an ISO day (YYYY-MM-DD), exactly what the native
   date input gave, so a screen swaps one for the other with no change to
   what it stores. An expiry field may take a month alone ("04/2027"),
   which means that month's last day. */

type DateFormatState = {
  /** The format this person types and reads dates in. */
  format: DateFormat;
  /** Where it comes from: the person's own choice or the hospital's. */
  source: 'mine' | 'hospital';
};

const DateFormatContext = createContext<DateFormatState>({ format: DEFAULT_DATE_FORMAT, source: 'hospital' });

export function DateFormatProvider({ value, children }: { value: DateFormatState; children: ReactNode }) {
  return <DateFormatContext.Provider value={value}>{children}</DateFormatContext.Provider>;
}

export function useDateFormat(): DateFormatState {
  return useContext(DateFormatContext);
}

type DateFieldProps = {
  label: string;
  /** An ISO day, or '' for none. */
  value: string;
  onChange: (iso: string) => void;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  /** An expiry: a month alone means its last day. */
  month?: boolean;
  /** Bounds as ISO days; a typed day outside them is said, not refused. */
  min?: string;
  max?: string;
  help?: string;
  error?: string;
  /** Overrides the format from context (a preview of another format). */
  format?: DateFormat;
  /** Lands on the input, as on TextField. */
  'data-testid'?: string;
  /** For a field inside a table row, whose column header is the label. */
  hideLabel?: boolean;
  /** A compact field inside a dense row. */
  size?: 'sm' | 'md';
  /** The grey text; the format (and the month form where allowed) unless
   *  a narrow field says less. */
  placeholder?: string;
  onEnter?: () => void;
};

export function DateField({
  label,
  value,
  onChange,
  name,
  required,
  disabled,
  autoFocus,
  month,
  min,
  max,
  help,
  error,
  format: formatOverride,
  'data-testid': testId,
  hideLabel,
  size = 'md',
  placeholder,
  onEnter,
}: DateFieldProps) {
  const { format: contextFormat } = useDateFormat();
  const format = formatOverride ?? contextFormat;
  const [text, setText] = useState(() => writeDay(value, format));
  const [touched, setTouched] = useState(false);
  /* The last day this field told its owner, so a value arriving from
     outside (a draft reopened, a scan filling the expiry) rewrites the text
     and the field's own echo of what was typed never does. */
  const emitted = useRef(value);
  /* The value as last handed in. Only a CHANGE of it rewrites the text: a
     parent that ignores a half-typed date keeps handing in the old day, and
     that must not wipe what the person is in the middle of typing. */
  const handed = useRef(value);
  const echoId = useId();

  useEffect(() => {
    if (value === handed.current) return;
    handed.current = value;
    if (value === emitted.current) return;
    emitted.current = value;
    setText(writeDay(value, format));
  }, [value, format]);

  /* A change of format rewrites the text the field shows, once. */
  const shownFormat = useRef(format);
  useEffect(() => {
    if (shownFormat.current === format) return;
    shownFormat.current = format;
    setText(writeDay(emitted.current, format));
  }, [format]);

  const read = readTypedDate(text, format, { month });

  function change(next: string) {
    setText(next);
    const parsed = readTypedDate(next, format, { month });
    const iso = parsed.kind === 'day' ? parsed.iso : '';
    if (iso !== emitted.current) {
      emitted.current = iso;
      onChange(iso);
    }
  }

  let echo: ReactNode = null;
  let tone: 'ok' | 'warn' | 'error' | 'quiet' = 'quiet';
  if (read.kind === 'day') {
    tone = 'ok';
    echo = read.monthOnly ? `End of ${monthInWords(read.iso)} (${dayInWords(read.iso)})` : dayInWords(read.iso);
    if (min && read.iso < min) {
      tone = 'warn';
      echo = `${echo}. That is before ${dayInWords(min)}`;
    } else if (max && read.iso > max) {
      tone = 'warn';
      echo = `${echo}. That is after ${dayInWords(max)}`;
    }
  } else if (read.kind === 'invalid' && (!read.incomplete || touched)) {
    tone = 'error';
    echo = read.incomplete ? `Not a date yet. Type it as ${formatHint(format, { month })}` : read.reason;
  }

  return (
    <label className={cn('mrd-field', 'mrd-datefield', `mrd-datefield--${size}`, (error || tone === 'error') && 'mrd-field--error')}>
      <span className={cn('mrd-field__label', hideLabel && 'mrd-datefield__srlabel mrd-sr-only')}>
        {label}
        {required && <span className="mrd-field__req"> *</span>}
      </span>
      <span className="mrd-field__control">
        <input
          className="mrd-field__input mrd-num mrd-datefield__input"
          type="text"
          inputMode="numeric"
          value={text}
          name={name}
          placeholder={placeholder ?? formatHint(format, { month })}
          autoComplete="off"
          autoFocus={autoFocus}
          required={required}
          disabled={disabled}
          aria-describedby={echo ? echoId : undefined}
          aria-invalid={tone === 'error' ? true : undefined}
          data-testid={testId}
          data-iso={value || undefined}
          onChange={(event) => change(event.target.value)}
          onBlur={() => setTouched(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && onEnter) {
              event.preventDefault();
              onEnter();
            }
          }}
        />
      </span>
      {echo ? (
        <span id={echoId} className={cn('mrd-datefield__echo', `is-${tone}`)} role={tone === 'error' ? 'alert' : undefined}>
          {echo}
        </span>
      ) : help && !error ? (
        <span className="mrd-field__help">{help}</span>
      ) : null}
      {error && <span className="mrd-field__message">{error}</span>}
    </label>
  );
}
